import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react'
import { FAQPage, Question as SchemaQuestion, WithContext } from 'schema-dts'
import { graphql, Link, Script } from 'gatsby'
import { WindowLocation } from '@reach/router'
import * as JsSearch from 'js-search'
import clsx from 'clsx'

import { HeroText, HeroTitle } from '../../components/hero'
import { PlainImage } from '../../components/image'
import { SearchIcon } from '../../components/icons'
import { FaqPageQuestions } from './faq-page-questions'
import { richTextToHtml } from '../../components/contentful-rich-text'
import { CategoryTitle } from '../../components/faq'
import {
  Container,
  Text,
  Title,
  Section,
  Background,
} from '../../components/page'
import { useSnippets } from '../../components/snippet'

import * as styles from './faq-page.module.scss'

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

export type FaqPageProps = Queries.FaqPageFragment & {
  location: WindowLocation
}

export const FaqPage = ({
  title,
  text,
  placeholder,
  categories,
  location,
}: FaqPageProps) => {
  const category = categories && categories?.length > 0 ? categories[0] : null
  const active = (location.hash || category?.slug || '').replace('#', '')

  /**
   * Create questions array
   */
  type Question = Queries.FaqFragment & { html: string }
  const questions = useMemo(
    () =>
      (categories || []).reduce<Question[]>(
        (questions, category) => [
          ...questions,
          ...(category?.questions || []).map<Question>((question) => ({
            ...(question as Queries.FaqFragment),
            html: richTextToHtml(question?.text),
          })),
        ],
        []
      ),
    [categories]
  )

  /**
   * Search logic
   *
   * @see https://github.com/bvaughn/js-search
   * @see https://www.gatsbyjs.com/docs/adding-search-with-js-search/
   */
  const [searchString, setSearchString] = useState('')
  const [searchResults, setSearchResults] = useState<Question[]>([])
  const isSearch = searchString.length > 0
  const s = useSnippets()

  const search = useRef<JsSearch.Search>(undefined!)
  if (!search.current) {
    search.current = new JsSearch.Search('id')
    search.current.addIndex('question')
    search.current.addIndex('html')
    search.current.addDocuments(questions)
  }

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const results = search.current.search(e.target.value) as Question[]
      setSearchString(e.target.value)
      setSearchResults(results)
    },
    []
  )

  const clearSearch = useCallback(() => {
    setSearchString('')
    setSearchResults([])
  }, [])

  /**
   * Create FAQ structured data
   *
   * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
   */
  const faqJsonLd: WithContext<FAQPage> = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: questions.map<SchemaQuestion>((question) => ({
        '@type': 'Question',
        name: question?.question!,
        acceptedAnswer: {
          '@type': 'Answer',
          text: question.html,
        },
      })),
    }),
    [categories]
  )

  /**
   * Apply active category after initial render.
   *
   * This causes an ugly blink on page load, until the active category is shown.
   * This is a limitation when doing SSG and there's nothing we can do about it.
   *
   * @todo If this is important for the client, we can prerender FAQ pages with unique urls:
   *        - /faq/getting-started/
   *        - /faq/borrowing/
   *        - /faq/lending/
   *        - /faq/listing/
   */
  const [mounted, setMounted] = useState(false)
  useIsomorphicLayoutEffect(() => {
    setMounted(true)
  }, [])

  return (
    <>
      <Script
        id="faq-json-ld"
        type="application/ld+json"
        //strategy="post-hydrate"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Section className={styles.hero} variant="hero">
        <Container>
          <HeroTitle color="light" center>
            {title}
          </HeroTitle>
          <HeroText color="light" center>
            {text?.text}
          </HeroText>
          {/*
          <form className={styles.form}>
            <input
              type="text"
              placeholder={placeholder!}
              value={searchString}
              onChange={onChange}
            />
            <button type="button">
              <SearchIcon />
            </button>
          </form>
          */}
        </Container>
        <Background className={styles.background} variant="ellipse" />
      </Section>

      <Section className={styles.list}>
        <Container className={styles.header}>
          {categories?.map((category) => (
            <Link
              key={category?.id}
              to={`/faq/#${category?.slug}`}
              data-category={category?.slug}
              className={clsx(
                styles.button,
                mounted &&
                  !isSearch &&
                  active === category?.slug &&
                  styles.selected
              )}
              onClick={clearSearch}
            >
              <PlainImage image={category?.image} />
              <CategoryTitle>{category?.title}</CategoryTitle>
            </Link>
          ))}
        </Container>
        <Container className={styles.categories}>
          <form className={styles.contentform}>
            <input
              type="text"
              placeholder={placeholder!}
              value={searchString}
              onChange={onChange}
            />
            <button type="button">
              <SearchIcon />
            </button>
          </form>

          {isSearch ? (
            <div key="search" className={clsx(styles.category, styles.active)}>
              <Title>{s.search_results}</Title>
              {searchResults.length === 0 ? (
                <Text>{s.search_no_results}</Text>
              ) : (
                <FaqPageQuestions questions={searchResults} />
              )}
            </div>
          ) : (
            categories?.map((category) => (
              <div
                key={category?.id}
                className={clsx(
                  styles.category,
                  mounted && active === category?.slug
                    ? styles.active
                    : styles.hidden
                )}
              >
                <Title>{category?.title}</Title>
                <Text>{category?.text?.text}</Text>
                <FaqPageQuestions
                  cat={category?.title!}
                  questions={category?.questions}
                  isActive={active === category?.slug}
                />
              </div>
            ))
          )}
        </Container>
      </Section>
    </>
  )
}

export const FaqPageFragments = graphql`
  fragment FaqPage on ContentfulFaqPage {
    __typename
    id
    title
    text {
      text
    }
    placeholder
    categories {
      ...FaqCategory
    }
  }
`
